import { Builder } from '@builder.io/react';
import { builder } from '@builder.io/react';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import ChevronDown from '@videoblocks/react-icons/ChevronDown';

import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

const fetchContent = async (urlSlug: string, modelName: string) => {
  // Fetch the content, otherwise fetch the default content
  const builderOptions = urlSlug
    ? {
        query: {
          'data.urlSlug': urlSlug,
        },
        options: {
          fields: 'data',
        },
      }
    : {};

  try {
    const builderApiResponse = await builder.get(modelName, builderOptions);
    return (
      builderApiResponse?.data || {
        faqItems: [],
      }
    );
  } catch (e) {
    return {
      faqItems: [],
    };
  }
};

const Faq = ({
  urlSlug,
  modelName = 'category-search-page',
}: {
  urlSlug?: string;
  modelName?: string;
}) => {
  const [items, setItems] = useState([]);
  const [accordion, setAccordion] = useState({});

  if (!urlSlug) return null;

  useEffect(() => {
    let isMounted = true;
    // If urlSlug is provided, fetch the content
    if (urlSlug) {
      const getContent = async () => {
        const { faqItems } = await fetchContent(urlSlug, modelName);
        if (isMounted) {
          setItems(faqItems || []);
        }
      };
      getContent();
    }
    return () => {
      isMounted = false;
    };
  }, [urlSlug, modelName]);

  const handleOnToggle = (section: string) => {
    setAccordion((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (items.length === 0) return null;

  return (
    <div className="font-polysans flex flex-col lg:flex-row w-full gap-8 px-6 py-16 md:py-24 md:px-12 lg:px-24">
      <div className="flex-auto lg:max-w-120 lg:w-full">
        <Text fontFamily="polysans" fontWeight="semibold" fontSize="5xl">
          <>
            <span className="lg:block">Frequently </span>Asked Questions
          </>
        </Text>
      </div>
      <ul className="flex-auto">
        {items.length > 0
          ? items.map(({ faqAnswer, faqQuestion }, index) => (
              <li
                className="flex flex-col w-full bg-gray-200 mb-6 p-8 rounded-3xl border-gray-300 border-2 cursor-pointer"
                key={`faq_${index}`}
                onClickCapture={() => handleOnToggle(`faq_${index}`)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && handleOnToggle(`faq_${index}`)
                }
                role="button"
                tabIndex={0}
                aria-label={`FAQ item: ${faqQuestion}`}
                aria-describedby={`faq-content-${index}`}
                aria-expanded={accordion[`faq_${index}`]}
              >
                <div
                  className="flex justify-between"
                  aria-controls={`faq-content-${index}`}
                >
                  <Text
                    className="text-gray-900 mb-0"
                    fontFamily="polysans"
                    fontSize="2xl"
                    fontWeight="semibold"
                  >
                    {faqQuestion}
                  </Text>
                  <div className="flex flex-none justify-center items-center w-30px h-30px bg-yellow-300 rounded-full border-2 border-blue-900">
                    <ChevronDown
                      className={twMerge(
                        'h-15px w-15px transition duration-200 ease-in',
                        accordion[`faq_${index}`] && 'transform -rotate-180'
                      )}
                    />
                  </div>
                </div>
                <div
                  id={`faq-content-${index}`}
                  className={twMerge(
                    accordion[`faq_${index}`] ? 'max-h-1000px' : 'max-h-0',
                    'overflow-hidden transition-max-height duration-200 ease-in-out'
                  )}
                  role="region"
                  aria-live="polite"
                  aria-labelledby={`faq-question-${index}`}
                >
                  <Text
                    className="text-gray-800 mt-2 mb-0"
                    fontFamily="polysans"
                    fontWeight="regular"
                    fontSize="base"
                  >
                    {faqAnswer}
                  </Text>
                </div>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
};

export default Faq;

Builder.registerComponent(Faq, {
  name: 'Faq SB',
  inputs: [],
});
